import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";

const AIChatFiltersContext = createContext({
  pageName: null,
  filters: [],
  setPageFilters: () => {},
  clearPageFilters: () => {},
  setChatOverride: () => {},
  clearChatOverride: () => {},
  requestOpenChat: () => {},
  subscribeOpenChat: () => () => {},
});

export function AIChatFiltersProvider({ children }) {
  const [pageDefault, setPageDefault] = useState({ name: null, filters: [] });
  const [chatOverride, setChatOverrideState] = useState(null);
  const openChatListenerRef = useRef(null);

  const setPageFilters = useCallback((name, filterList) => {
    setPageDefault({ name, filters: filterList || [] });
  }, []);

  const clearPageFilters = useCallback(() => {
    setPageDefault({ name: null, filters: [] });
    setChatOverrideState(null);
  }, []);

  const setChatOverride = useCallback((name, filterList) => {
    setChatOverrideState({ name, filters: filterList || [] });
  }, []);

  const clearChatOverride = useCallback(() => {
    setChatOverrideState(null);
  }, []);

  const requestOpenChat = useCallback(() => {
    if (openChatListenerRef.current) {
      openChatListenerRef.current();
    }
  }, []);

  const subscribeOpenChat = useCallback((listener) => {
    openChatListenerRef.current = listener;
    return () => {
      openChatListenerRef.current = null;
    };
  }, []);

  const pageName = chatOverride ? chatOverride.name : pageDefault.name;
  const filters = chatOverride ? chatOverride.filters : pageDefault.filters;

  const value = useMemo(
    () => ({
      pageName,
      filters,
      setPageFilters,
      clearPageFilters,
      setChatOverride,
      clearChatOverride,
      requestOpenChat,
      subscribeOpenChat,
    }),
    [
      pageName,
      filters,
      setPageFilters,
      clearPageFilters,
      setChatOverride,
      clearChatOverride,
      requestOpenChat,
      subscribeOpenChat,
    ],
  );

  return (
    <AIChatFiltersContext.Provider value={value}>
      {children}
    </AIChatFiltersContext.Provider>
  );
}

AIChatFiltersProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAIChatFilters() {
  return useContext(AIChatFiltersContext);
}
